import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Field, inputCls } from "@/components/ui-primitives";
import { Trash2, ClipboardCheck, Camera } from "lucide-react";
import PhotoUpload, { PhotoThumbs } from "@/components/PhotoUpload";

const emptyForm = {
  equipment_id: "",
  interval: "Daily",
  tech: "",
  date: new Date().toISOString().split("T")[0],
  notes: "",
};

const STATUS_OPTIONS = ["PASS", "FAIL", "N/A"];

export default function PMChecklist() {
  const [equipment, setEquipment] = useState([]);
  const [records, setRecords] = useState([]);
  const [meta, setMeta] = useState({ pm_intervals: [], checklist_tasks: {} });
  const [form, setForm] = useState(emptyForm);
  const [checklist, setChecklist] = useState([]);
  const [attachments, setAttachments] = useState([]);

  const load = async () => {
    try {
      const [eqRes, pmRes, metaRes] = await Promise.all([
        api.get("/equipment"),
        api.get("/pm-records"),
        api.get("/meta"),
      ]);
      setEquipment(eqRes.data);
      setRecords(pmRes.data);
      setMeta(metaRes.data);
      if (!form.equipment_id && eqRes.data.length > 0) {
        const first = eqRes.data[0];
        setForm((f) => ({ ...f, equipment_id: first.id }));
        setChecklist((metaRes.data.checklist_tasks[first.type] || []).map((task) => ({ task, status: "PASS" })));
      }
    } catch { toast.error("Failed to load"); }
  };

  useEffect(() => { load(); }, []);

  const onEquipmentChange = (id) => {
    setForm({ ...form, equipment_id: id });
    const eq = equipment.find((e) => e.id === id);
    if (eq) {
      const tasks = meta.checklist_tasks[eq.type] || [];
      setChecklist(tasks.map((task) => ({ task, status: "PASS" })));
    } else {
      setChecklist([]);
    }
  };

  const setStatus = (idx, status) => {
    setChecklist((cl) => cl.map((c, i) => (i === idx ? { ...c, status } : c)));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.equipment_id) { toast.error("Select equipment"); return; }
    try {
      await api.post("/pm-records", { ...form, checklist, attachments });
      toast.success("PM record saved");
      const currentEqId = form.equipment_id;
      setForm({ ...emptyForm, equipment_id: currentEqId });
      setAttachments([]);
      const eq = equipment.find((e) => e.id === currentEqId);
      const tasks = eq ? meta.checklist_tasks[eq.type] || [] : [];
      setChecklist(tasks.map((task) => ({ task, status: "PASS" })));
      load();
    } catch { toast.error("Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this PM record?")) return;
    try {
      await api.delete(`/pm-records/${id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const equipmentLabel = (id) => {
    const e = equipment.find((x) => x.id === id);
    return e ? `${e.type} · ${e.asset_id}` : "—";
  };

  return (
    <div className="p-8 max-w-[1800px] mx-auto">
      <PageHeader eyebrow="Preventive Maintenance" title="PM" accent="Checklist" />

      <form onSubmit={submit} className="bg-[#121212] border border-white/10 p-5 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-5">
          <Field label="Equipment *">
            <select data-testid="pm-equipment" value={form.equipment_id} onChange={(e) => onEquipmentChange(e.target.value)} className={inputCls}>
              {equipment.length === 0 && <option value="">— No equipment —</option>}
              {equipment.map((e) => (<option key={e.id} value={e.id}>{e.type} · {e.asset_id}</option>))}
            </select>
          </Field>
          <Field label="Interval">
            <select data-testid="pm-interval" value={form.interval} onChange={(e) => setForm({ ...form, interval: e.target.value })} className={inputCls}>
              {meta.pm_intervals.map((i) => (<option key={i} value={i}>{i}</option>))}
            </select>
          </Field>
          <Field label="Technician">
            <input data-testid="pm-tech" value={form.tech} onChange={(e) => setForm({ ...form, tech: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Date *">
            <input type="date" data-testid="pm-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
          </Field>
        </div>

        {checklist.length > 0 && (
          <div className="mb-5">
            <div className="font-mono-tab text-[10px] tracking-[0.2em] uppercase text-[#737373] mb-3 flex items-center gap-2">
              <ClipboardCheck className="w-3.5 h-3.5" /> Inspection Checklist ({checklist.length} items)
            </div>
            <div data-testid="pm-checklist" className="border border-white/10 divide-y divide-white/5">
              {checklist.map((c, i) => (
                <div key={i} className="flex items-center justify-between px-4 py-2.5 bg-[#0A0A0A]/50">
                  <span className="text-sm text-[#E0E0E0]">{c.task}</span>
                  <div className="flex items-center gap-1">
                    {STATUS_OPTIONS.map((s) => {
                      const active = c.status === s;
                      const color = s === "PASS" ? "border-[#22C55E]/50 bg-[#22C55E]/15 text-[#22C55E]"
                                  : s === "FAIL" ? "border-[#FF3B30]/50 bg-[#FF3B30]/15 text-[#FF3B30]"
                                  : "border-white/20 bg-white/5 text-[#A3A3A3]";
                      return (
                        <button key={s} type="button" data-testid={`pm-cl-${i}-${s}`} onClick={() => setStatus(i, s)}
                          className={`px-2.5 py-1 text-[10px] font-mono-tab tracking-wider border transition-all ${
                            active ? color : "border-white/10 text-[#737373] hover:border-white/30"
                          }`}>
                          {s}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mb-5">
          <div className="font-mono-tab text-[10px] tracking-[0.2em] uppercase text-[#737373] mb-2 flex items-center gap-2">
            <Camera className="w-3.5 h-3.5" /> Photo Attachments (optional)
          </div>
          <PhotoUpload testid="pm-photos" value={attachments} onChange={setAttachments} />
        </div>

        <div className="mb-5">
          <Field label="Notes / Readings / Actions Taken">
            <textarea data-testid="pm-notes" rows="3" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} />
          </Field>
        </div>

        <div className="flex justify-end">
          <button data-testid="pm-save-btn" type="submit" className="px-6 py-2.5 bg-[#007AFF] hover:bg-[#3395FF] text-white text-sm font-medium">
            Save PM Record
          </button>
        </div>
      </form>

      <div className="border border-white/10 bg-[#121212] overflow-x-auto">
        <table data-testid="pm-table" className="w-full text-sm">
          <thead className="bg-black/60 backdrop-blur-xl">
            <tr className="text-left font-mono-tab text-[10px] tracking-[0.2em] uppercase text-[#737373]">
              <th className="px-4 py-3 border-b border-white/10">Date</th>
              <th className="px-4 py-3 border-b border-white/10">Equipment</th>
              <th className="px-4 py-3 border-b border-white/10">Interval</th>
              <th className="px-4 py-3 border-b border-white/10">Tech</th>
              <th className="px-4 py-3 border-b border-white/10 text-right">Pass</th>
              <th className="px-4 py-3 border-b border-white/10 text-right">Fail</th>
              <th className="px-4 py-3 border-b border-white/10 text-right">N/A</th>
              <th className="px-4 py-3 border-b border-white/10 text-center">Photos</th>
              <th className="px-4 py-3 border-b border-white/10">Notes</th>
              <th className="px-4 py-3 border-b border-white/10 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {records.length === 0 && (<tr><td colSpan={10} className="px-4 py-12 text-center text-[#A3A3A3]">No PM records yet.</td></tr>)}
            {records.map((p) => (
              <tr key={p.id} className="border-b border-white/5 hover:bg-white/[0.03]">
                <td className="px-4 py-3 font-mono-tab text-xs text-[#E0E0E0]">{p.date}</td>
                <td className="px-4 py-3 text-white">{equipmentLabel(p.equipment_id)}</td>
                <td className="px-4 py-3">
                  <span className="inline-block px-2 py-0.5 text-[10px] font-mono-tab tracking-[0.15em] uppercase border border-white/10 bg-white/5 text-[#E0E0E0]">
                    {p.interval}
                  </span>
                </td>
                <td className="px-4 py-3 text-[#E0E0E0]">{p.tech || "—"}</td>
                <td className="px-4 py-3 text-right font-mono-tab text-[#22C55E]">{p.pass_count}</td>
                <td className="px-4 py-3 text-right font-mono-tab text-[#FF3B30]">{p.fail_count}</td>
                <td className="px-4 py-3 text-right font-mono-tab text-[#A3A3A3]">{p.na_count}</td>
                <td className="px-4 py-3 text-center font-mono-tab text-xs text-[#E0E0E0]">
                  {p.attachments?.length > 0 ? (
                    <span className="inline-flex items-center gap-1 text-[#007AFF]">
                      <Camera className="w-3 h-3" /> {p.attachments.length}
                    </span>
                  ) : "—"}
                </td>
                <td className="px-4 py-3 text-[#E0E0E0] max-w-xs truncate">{p.notes || "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end">
                    <button data-testid={`pm-del-${p.id}`} onClick={() => remove(p.id)} className="p-1.5 border border-white/10 hover:border-[#FF3B30]/40 hover:bg-[#FF3B30]/10">
                      <Trash2 className="w-3.5 h-3.5 text-[#FF3B30]" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
